/**
 * Ephemeral, in-memory marker for "this alert was JUST subscribed by the user
 * in this tab session."
 *
 * Why this exists: the sidebar plays a pop+shake entrance on a newly-subscribed
 * row. But on a user's FIRST-EVER subscription the whole "Subscribed Alerts"
 * section — and the list component inside it — MOUNTS FRESH (it's hidden when
 * count === 0). A plain "diff current ids vs previous ids" can't detect the new
 * row in that case, because the very first snapshot the freshly-mounted list
 * sees already contains the row, so it gets treated as part of the baseline and
 * never animates.
 *
 * The subscribe action marks the alert id here; the sidebar treats any row
 * whose id is marked as "just added" and animates it once, then consumes it.
 *
 * Module-level Map (not sessionStorage) on purpose:
 *  - Resets on a full page reload → a reload NEVER replays the animation.
 *  - Entries self-expire after a short window so a slow section mount still
 *    animates, but a stale id can never animate later.
 */

const recent = new Map<string, number>();
const DEFAULT_WINDOW_MS = 8000;

/** Flag an alert id as just-subscribed (call right after a successful subscribe). */
export function markRecentlySubscribed(alertId: string): void {
  if (!alertId) return;
  recent.set(alertId, Date.now());
}

/** True if the id was subscribed within `windowMs`. Pure read — does not mutate. */
export function wasRecentlySubscribed(
  alertId: string,
  windowMs: number = DEFAULT_WINDOW_MS,
): boolean {
  const t = recent.get(alertId);
  if (t === undefined) return false;
  return Date.now() - t <= windowMs;
}

/** Clear the flag once the row has animated, so it never re-animates. */
export function consumeRecentlySubscribed(alertId: string): void {
  recent.delete(alertId);
}
